import React, { useEffect, useMemo, useRef } from 'react';
import { convertTextToURL, escapeHtml, getFormattedText } from '../../../../../Helpers/Utility';
import { handleMentionedUser } from '../../../../../Helpers/Chat/User';

export const useMessageProcessing = ({ messageObject, isSender, vCardData, htmlToReactParser }) => {
    const { msgBody = {} } = messageObject || {};
    const { data = {} } = vCardData || {};

    // Reusable processing logic
    const processText = React.useCallback(
        (text, mentionedList = null) => {
            if (!text) return { safeText: '', formattedText: null };

            const originalText = text;
            let cleanedOriginalText = originalText;

            if (originalText && typeof originalText === 'string') {
                cleanedOriginalText = originalText.replace(
                    /(<a\s+[^>]*href=["']([^"']+)["'][^>]*>)([^<]*?)(<\/a>)\2/gi,
                    '$1$3$4',
                );
                cleanedOriginalText = cleanedOriginalText.replace(/(<\/a>)(https?:\/\/[^\s<>"']+)/gi, '$1');
                cleanedOriginalText = cleanedOriginalText.replace(/(<\/a>)(www\.[^\s<>"']+)/gi, '$1');
            }

            // Use generic conversion if not main message link (or reuse logic)
            // For main message, 'messageLink' is passed in but here we recalculate for arbitrary text
            const textData = cleanedOriginalText ? convertTextToURL(cleanedOriginalText) : text;

            const mentionedUsers = mentionedList ? mentionedList : msgBody.mentionedUsersIds || [];

            const rawText = getFormattedText(textData);
            const containsMention = /@\[\?\]/.test(rawText);
            const containsAngleBrackets = /</.test(rawText);
            const hasValidAnchorTags = /<a\s+[^>]*href=["'][^"']+["'][^>]*>.*?<\/a>/i.test(rawText);

            const processedText = handleMentionedUser({
                text: rawText,
                mentionedUsersIds: mentionedUsers,
                mentionedMe: !isSender && data.userId,
                mentionedClass: null,
                chatType: messageObject?.chatType,
            });

            let finalSafeText =
                containsAngleBrackets && !containsMention && !hasValidAnchorTags ? escapeHtml(rawText) : processedText;

            if (hasValidAnchorTags) {
                finalSafeText = finalSafeText.replace(/(<\/a>)\s*(https?:\/\/|www\.)[^\s<>"']+/gi, '$1');
                finalSafeText = finalSafeText.replace(
                    /(<a\s+[^>]*href=["']([^"']+)["'][^>]*>)([^<]*?)(<\/a>)\s*\2/gi,
                    '$1$3$4',
                );
            }

            return {
                safeText: finalSafeText,
                formattedText: htmlToReactParser?.parse(finalSafeText),
            };
        },
        [msgBody.mentionedUsersIds, isSender, data.userId, messageObject, htmlToReactParser],
    );

    // Calculate main message formattedText
    const contentToProcess = msgBody.message || msgBody.media?.caption || '';

    const { formattedText, safeText } = useMemo(() => {
        return processText(contentToProcess);
    }, [processText, contentToProcess]);

    /*
     * Runtime Validation:
     * Inspects the rendered DOM and compares it against expected patterns and the 'safeText'.
     */
    const messageRef = useRef(null);

    useEffect(() => {
        if (messageRef.current) {
            const renderedInnerText = messageRef.current.innerText;
            const originalText = contentToProcess;

            // 1. Check for persistent Mention Placeholders
            if (originalText.includes('@[?]') && renderedInnerText.includes('@[?]')) {
                console.error('[useMessageProcessing] Rendering Error: Mention placeholder @[?] persisted in UI.', {
                    type: 'MENTION_FAILURE',
                    messageId: messageObject?.msgId,
                    original: originalText,
                    rendered: renderedInnerText,
                    messageObject,
                    vCardData,
                });
            }

            // 2. Check for Unparsed HTML Tags visible to user
            const suspiciousTags = ['<b>', '</b>', '<i>', '</i>', 'href=', '</a>'];
            const foundSuspicious = suspiciousTags.find(tag => renderedInnerText.includes(tag));
            if (foundSuspicious) {
                console.error(
                    `[useMessageProcessing] Rendering Error: Unparsed HTML tag '${foundSuspicious}' visible in text.`,
                    {
                        type: 'HTML_LEAK',
                        messageId: messageObject?.msgId,
                        rendered: renderedInnerText,
                        messageObject,
                        vCardData,
                    },
                );
            }

            // 3. Comparison Check (Heuristic)
            if (safeText && safeText.length > 0 && (!renderedInnerText || renderedInnerText.trim().length === 0)) {
                // Warning logic (commented out to avoid noise, but structurally here)
                // console.warn("Rendering Warning: Output appears empty despite content.");
            }
        }
    }, [messageObject, contentToProcess, safeText]); // Added contentToProcess and safeText to dependencies

    return {
        messageRef,
    };
};
